import { useMemo } from "react";

import {
  PRACTICE_NAME_IRRIGATED,
  PRACTICE_NAME_NON_IRRIGATED,
} from "@datanac/datanac-api-toolkit/dist/datanac-rma-api-helper";

const TAB_LABELS = {
  IRRIGATED: "Irrigated",
  NON_IRRIGATED: "Non-Irrigated",
  COMBINED: "Combined",
};

const TAB_KEYS = {
  IRRIGATED: "irrigated",
  NON_IRRIGATED: "non-irrigated",
  COMBINED: "combined",
};

/**
 * Hook for determining available tabs for ARC-CO calculations based on practice types.
 * Creates tabs for irrigated, non-irrigated, and combined calculations when applicable.
 *
 * @param {Array} arcCoCalculations - Array of ARC-CO calculations by practice type
 * @param {Object} combinedCalculation - Combined weighted calculation object
 * @returns {Array} Array of tab objects with label, calc, and key properties
 */
export const useArcCoTabs = (arcCoCalculations, combinedCalculation) => {
  return useMemo(() => {
    if (!arcCoCalculations?.length) {
      return [];
    }

    // Find specific practice calculations
    const irrigatedCalc = arcCoCalculations.find((calc) => calc.practice_name === PRACTICE_NAME_IRRIGATED);
    const nonIrrigatedCalc = arcCoCalculations.find((calc) => calc.practice_name === PRACTICE_NAME_NON_IRRIGATED);
    const hasMultiplePractices = irrigatedCalc && nonIrrigatedCalc;

    // Find any other calculations that don't match irrigated/non-irrigated practice names
    const otherCalcs = arcCoCalculations.filter(
      (calc) => calc.practice_name !== PRACTICE_NAME_IRRIGATED && calc.practice_name !== PRACTICE_NAME_NON_IRRIGATED
    );

    const tabs = [];

    // Add irrigated tab if calculation exists
    if (irrigatedCalc) {
      tabs.push({
        label: TAB_LABELS.IRRIGATED,
        calc: irrigatedCalc,
        key: TAB_KEYS.IRRIGATED,
      });
    }

    // Add non-irrigated tab if calculation exists
    if (nonIrrigatedCalc) {
      tabs.push({
        label: TAB_LABELS.NON_IRRIGATED,
        calc: nonIrrigatedCalc,
        key: TAB_KEYS.NON_IRRIGATED,
      });
    }

    // Add tabs for other practice types (e.g., counties that don't require irrigation differences)
    otherCalcs.forEach((calc) => {
      tabs.push({
        label: calc.practice_name || "General",
        calc: calc,
        key: calc.practice_name?.toLowerCase().replace(/[^a-z0-9]/g, "-") || "general",
      });
    });

    // Add combined tab if both practices exist and combined calculation is available
    if (hasMultiplePractices && combinedCalculation) {
      tabs.push({
        label: TAB_LABELS.COMBINED,
        calc: combinedCalculation,
        key: TAB_KEYS.COMBINED,
      });
    }

    return tabs;
  }, [arcCoCalculations, combinedCalculation]);
};
